import { useAccountContext } from '@/context/account'
import { useCrosschainDepositExecute } from '@/queries/crosschainDepositExecute.query'
import type { CrosschainQuote } from '@/queries/crosschainQuote.query'
import { useState } from 'react'
import { type Address, type Hex, UserRejectedRequestError } from 'viem'
import { useConfig, useSignTypedData } from 'wagmi'
import { sendTransaction, switchChain, waitForTransactionReceipt } from 'wagmi/actions'

export function useCrosschainDeposit(crosschainQuote: CrosschainQuote | undefined) {
  const { mutateAsync, isPending, error: signTypedDataError } = useSignTypedData()
  const {
    mutateAsync: executeDepositMutation,
    isPending: isExecutePending,
    error: mutationError,
  } = useCrosschainDepositExecute()
  const [executeError, setExecuteError] = useState<Error | null>(null)
  const [crosschainTransactionId, setCrosschainTransactionId] = useState<string | undefined>(undefined)
  const [currentStep, setCurrentStep] = useState(0)
  const wagmiConfig = useConfig()
  const { chainId: connectedChainId, isConnected } = useAccountContext()

  const hasSetupTransactions =
    crosschainQuote?.setupTransactions && Object.keys(crosschainQuote.setupTransactions).length > 0

  const totalSteps = hasSetupTransactions ? 2 : 1

  async function executeDeposit() {
    setExecuteError(null)
    setCrosschainTransactionId(undefined)
    setCurrentStep(0)

    try {
      if (!isConnected) {
        throw new Error('Wallet not connected')
      }

      if (!crosschainQuote?.payloadToSign) {
        throw new Error('No payloadToSign in crosschain quote')
      }

      const sourceChainId = crosschainQuote.payloadToSign.domain.chainId
      if (connectedChainId !== sourceChainId) {
        await switchChain(wagmiConfig, { chainId: sourceChainId })
      }

      if (hasSetupTransactions) {
        setCurrentStep(1)
        const setupTxs = crosschainQuote.setupTransactions![sourceChainId.toString()]
        if (setupTxs && setupTxs.length > 0) {
          for (const tx of setupTxs) {
            const hash = await sendTransaction(wagmiConfig, {
              to: tx.to as Address,
              data: tx.data as Hex,
              chainId: tx.chainId,
              value: tx.value ? BigInt(tx.value) : undefined,
            })
            await waitForTransactionReceipt(wagmiConfig, { hash })
          }
        }
      }

      setCurrentStep(totalSteps)
      let signature: Hex
      try {
        signature = await mutateAsync({ ...crosschainQuote.payloadToSign })
      } catch (err) {
        if (
          err instanceof UserRejectedRequestError ||
          (err as { shortMessage?: string })?.shortMessage === 'User rejected the request.'
        ) {
          setCurrentStep(0)
          return
        }

        throw new Error(`Signature failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
      }

      const result = await executeDepositMutation({
        payloadSignature: signature,
        originalQuote: crosschainQuote.originalQuote,
      })

      if (result.crosschainTransactionId) {
        setCrosschainTransactionId(result.crosschainTransactionId)
      }
    } catch (error) {
      const errorToSet = error instanceof Error ? error : new Error('Unknown error occurred')
      setExecuteError(errorToSet)
      setCurrentStep(0)

      throw errorToSet
    }
  }

  return {
    executeDeposit,
    isPending: isPending || isExecutePending,
    error: mutationError || signTypedDataError || executeError,
    crosschainTransactionId,
    clearTransaction: () => setCrosschainTransactionId(undefined),
    currentStep,
    totalSteps,
  }
}
